import {Box, createStyles, Flex, Group, Text, Tooltip} from '@mantine/core';
import React, {useState} from 'react';

import {exportAuditLogs} from 'audit/AuditActions';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelectorModal from 'file-browser/FolderSelectorModal';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import MantineOverflowMenu from 'ui-library/menus/MantineOverflowMenu';
import {CenteredSpinner} from 'ui-library/Spinner';

import type {
  CanvasFolderArtifactFileStateQuery,
  CanvasFolderArtifactFileStateQueryVariables,
} from '../backbone/__generated/CanvasFolderArtifactFileStateQuery';
import {
  CANVAS_FOLDER_ARTIFACT_FILE_STATE_QUERY,
  CanvasFolderArtifactFileStateGraphQL,
} from '../backbone/CanvasFolderArtifactGraphQL';
import CanvasPreviewFrame from '../preview/CanvasPreviewFrame';
import useCanvasExpandedPreviewUrl from '../preview/useCanvasExpandedPreviewUrl';
import CanvasFolderArtifactMetadataModal from './CanvasFolderArtifactMetadataModal';

const useStyles = createStyles((theme) => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
    overflow: 'auto',
  },
  headerRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  headerActions: {
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing.sm,
  },
  previewSection: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 480,
  },
}));

function handleMoveArtifact(
  artifact: CanvasFolderArtifactFileStateGraphQL,
  refetch: () => Promise<unknown>
): void {
  FolderSelectorModal.openForMove(
    {
      allowNone: false,
      initialSelectedFolderId: artifact.folder.id,
      itemsToMove: [{id: artifact.id, name: artifact.name}],
      showInventory: true,
      showNotebook: true,
      showRegistries: false,
    },
    (folderId) => {
      FileBrowserActions.moveItemsWithDataMods([artifact.id], folderId, {
        parentFolderIds: [artifact.folder.id],
      }).then(() => refetch());
    }
  );
}

function CanvasFolderArtifactViewContent({
  artifact,
  refetch,
}: {
  artifact: CanvasFolderArtifactFileStateGraphQL;
  refetch: () => Promise<unknown>;
}): JSX.Element {
  const {classes} = useStyles();
  const [refreshToken, setRefreshToken] = useState(0);
  const {signedUrl, isLoading, errorMessage} = useCanvasExpandedPreviewUrl(
    {canvasFolderArtifactId: artifact.id},
    refreshToken
  );

  const handleReset = (): void => {
    setRefreshToken((token) => token + 1);
  };

  const handleViewMetadata = (): void => {
    legacyRunModal(CanvasFolderArtifactMetadataModal, {artifact});
  };

  const handleViewAuditLog = (): void => {
    exportAuditLogs([artifact.id]);
  };

  const handleMove = (): void => {
    handleMoveArtifact(artifact, refetch);
  };

  return (
    <div className={classes.page} {...getTestProps({component: 'CanvasFolderArtifactView', element: 'root'})}>
      <Flex className={classes.headerRow}>
        <Text className={classes.title}>{artifact.name}</Text>
        <Group className={classes.headerActions} noWrap={true}>
          <MantineOverflowMenu
            sections={[
              {
                items: [
                  {
                    name: 'View metadata',
                    icon: 'info-circle',
                    onSelect: handleViewMetadata,
                  },
                  {
                    name: 'View audit log',
                    icon: 'audit-trail',
                    onSelect: handleViewAuditLog,
                  },
                  ...(artifact.permissions.movable
                    ? [
                        {
                          name: 'Move',
                          icon: 'move' as const,
                          onSelect: handleMove,
                        },
                      ]
                    : []),
                ],
              },
            ]}
          />
          <Tooltip label={isLoading ? 'Resetting...' : 'Reset'}>
            <span>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.SMALL}
                onClick={handleReset}
                disabled={isLoading}
                icon="refresh"
                aria-label="Reset"
                {...getTestProps({component: 'CanvasFolderArtifactView', element: 'resetButton'})}
              />
            </span>
          </Tooltip>
        </Group>
      </Flex>
      <Box className={classes.previewSection}>
        {isLoading ? (
          <CenteredSpinner />
        ) : errorMessage != null ? (
          <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
        ) : signedUrl != null ? (
          <CanvasPreviewFrame signedUrl={signedUrl} />
        ) : (
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load the canvas." />
        )}
      </Box>
    </div>
  );
}

export default function CanvasFolderArtifactView({
  canvasFolderArtifactId,
}: {
  canvasFolderArtifactId: string;
}): JSX.Element {
  const queryResult = useBenchlingQuery<
    CanvasFolderArtifactFileStateQuery,
    CanvasFolderArtifactFileStateQueryVariables
  >(CANVAS_FOLDER_ARTIFACT_FILE_STATE_QUERY, {
    variables: {canvasFolderArtifactId},
  });

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  if (queryResult.type !== QueryResultType.SUCCESS) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load the artifact." />;
  }
  const artifact = queryResult.data.canvasFolderArtifacts[0];
  if (artifact == null || isInaccessible(artifact)) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load the artifact." />;
  }
  return <CanvasFolderArtifactViewContent artifact={artifact} refetch={queryResult.refetch} />;
}
